import { useState } from 'react'
import { VStack, HStack, Card, Text, Heading, Button, Modal, Input, Skeleton, useToast } from 'wss3-forge'
import {
  Settings20Regular,
  ArrowDownload20Regular,
  Delete20Regular,
  Warning20Regular,
  Dismiss20Regular
} from '@fluentui/react-icons'
import type { AccountDeletionRequest, UserDataExport } from '../../types/database'

interface AccountManagementCardProps {
  deletionRequest: AccountDeletionRequest | null
  loading: boolean
  onExportData: () => Promise<{ data: UserDataExport | null; error: Error | null }>
  onRequestDeletion: (reason?: string) => Promise<{ data: unknown; error: Error | null }>
  onCancelDeletion: () => Promise<{ error: Error | null }>
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

function getDaysUntilDeletion(scheduledAt: string): number {
  const scheduled = new Date(scheduledAt)
  const now = new Date()
  const diff = scheduled.getTime() - now.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function AccountManagementCard({
  deletionRequest,
  loading,
  onExportData,
  onRequestDeletion,
  onCancelDeletion
}: AccountManagementCardProps) {
  const toast = useToast()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      const { data, error } = await onExportData()
      if (error) throw error
      if (!data) throw new Error('No data returned')

      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `webba-id-data-export-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)

      toast.success('Data exported successfully')
    } catch (err: any) {
      console.error('Export error:', err)
      toast.error(err.message || 'Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  const handleRequestDeletion = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm')
      return
    }

    setDeleting(true)
    try {
      const { error } = await onRequestDeletion(deleteReason || undefined)
      if (error) throw error

      toast.success('Account deletion scheduled')
      setShowDeleteModal(false)
      setDeleteReason('')
      setConfirmText('')
    } catch (err: any) {
      console.error('Deletion request error:', err)
      toast.error(err.message || 'Failed to request deletion')
    } finally {
      setDeleting(false)
    }
  }

  const handleCancelDeletion = async () => {
    if (!confirm('Cancel your account deletion request?')) return

    setCancelling(true)
    try {
      const { error } = await onCancelDeletion()
      if (error) throw error

      toast.success('Account deletion cancelled')
    } catch (err: any) {
      console.error('Cancel deletion error:', err)
      toast.error(err.message || 'Failed to cancel deletion')
    } finally {
      setCancelling(false)
    }
  }

  if (loading) {
    return (
      <Card padding="md">
        <VStack gap="md">
          <HStack gap="sm" style={{ alignItems: 'center' }}>
            <Settings20Regular style={{ color: 'var(--brand-primary)' }} />
            <Heading level={5}>Account Management</Heading>
          </HStack>
          <Skeleton width="100%" height={80} />
        </VStack>
      </Card>
    )
  }

  return (
    <>
      <Card padding="md">
        <VStack gap="md">
          <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
            <Settings20Regular style={{ color: 'var(--brand-primary)' }} />
            <Heading level={5}>Account Management</Heading>
          </HStack>

          {/* Pending deletion warning */}
          {deletionRequest && (
            <Card
              variant="outlined"
              padding="md"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderColor: 'var(--color-error)'
              }}
            >
              <VStack gap="sm">
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Warning20Regular style={{ color: 'var(--color-error)' }} />
                  <Text weight="medium" style={{ color: 'var(--color-error)' }}>
                    Account deletion scheduled
                  </Text>
                </HStack>
                <Text size="sm">
                  Your account will be permanently deleted on {formatDate(deletionRequest.scheduled_deletion_at)}.
                  You have {getDaysUntilDeletion(deletionRequest.scheduled_deletion_at)} days to cancel this request.
                </Text>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Dismiss20Regular />}
                  onClick={handleCancelDeletion}
                  loading={cancelling}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Cancel deletion
                </Button>
              </VStack>
            </Card>
          )}

          {/* Data Export */}
          <div style={{
            padding: '0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px'
          }}>
            <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <VStack gap="xs">
                <Text size="sm" weight="medium">Export your data</Text>
                <Text size="xs" color="muted">
                  Download a copy of all your personal data in JSON format
                </Text>
              </VStack>
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowDownload20Regular />}
                onClick={handleExport}
                loading={exporting}
              >
                Export
              </Button>
            </HStack>
          </div>

          {/* Delete Account */}
          {!deletionRequest && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px'
            }}>
              <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <VStack gap="xs">
                  <Text size="sm" weight="medium" style={{ color: 'var(--color-error)' }}>
                    Delete account
                  </Text>
                  <Text size="xs" color="muted">
                    Permanently delete your account and all associated data
                  </Text>
                </VStack>
                <Button
                  variant="danger"
                  size="sm"
                  icon={<Delete20Regular />}
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              </HStack>
            </div>
          )}
        </VStack>
      </Card>

      {/* Delete Account Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Account"
      >
        <VStack gap="md">
          <Card
            variant="outlined"
            padding="md"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderColor: 'var(--color-error)'
            }}
          >
            <HStack gap="sm" style={{ alignItems: 'flex-start' }}>
              <Warning20Regular style={{ color: 'var(--color-error)', marginTop: '2px' }} />
              <VStack gap="xs">
                <Text weight="medium" style={{ color: 'var(--color-error)' }}>
                  This action cannot be undone
                </Text>
                <Text size="sm">
                  Your account will be scheduled for deletion in 30 days. During this period, you can cancel the deletion request.
                </Text>
              </VStack>
            </HStack>
          </Card>

          <Text size="sm">
            Once your account is deleted:
          </Text>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-secondary)' }}>
            <li><Text size="sm">All your personal data will be permanently removed</Text></li>
            <li><Text size="sm">You will lose access to all connected applications</Text></li>
            <li><Text size="sm">Your subscriptions will be cancelled</Text></li>
            <li><Text size="sm">This email address cannot be used to create a new account</Text></li>
          </ul>

          <Input
            label="Reason for leaving (optional)"
            placeholder="Tell us why you're leaving..."
            value={deleteReason}
            onChange={(v) => setDeleteReason(v as string)}
          />

          <Input
            label='Type "DELETE" to confirm'
            placeholder="DELETE"
            value={confirmText}
            onChange={(v) => setConfirmText(v as string)}
          />

          <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRequestDeletion}
              loading={deleting}
              disabled={confirmText !== 'DELETE'}
            >
              Delete my account
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </>
  )
}
